import type { ExpAdmission, ExpExactEvidence, GameStateSnapshot } from '@/domain/types'
import { expRequirementRange, intersectExpRequirementRanges, representativeExpRequirement, type ExpRequirementRange } from './exp-requirement-estimator'

type Risk = NonNullable<ExpAdmission['reason']>
interface Interval { readonly at: number; readonly delta: number; readonly seconds: number }
interface Pending {
  first: ExpExactEvidence
  last: ExpExactEvidence
  range: ExpRequirementRange | null
  reason: Risk
  count: number
  credit: number
  threshold: number
}

export interface ExpAdmissionCheckpoint {
  readonly anchor: ExpExactEvidence | null
  readonly ranges: readonly (readonly [number, ExpRequirementRange])[]
  readonly intervals: readonly Interval[]
  readonly reference: { readonly delta: number; readonly rate: number } | null
  readonly total: number
  readonly epoch: number
  readonly epochStartTotal: number
  readonly epochStartAt: number
  readonly rejectedGap: { readonly level: number; readonly threshold: number } | null
}

/** Admission controls both endpoint adoption and earnings; repeated OCR never authorizes a rejected gap. */
export class ExpAdmissionFilter {
  private anchor: ExpExactEvidence | null = null
  private ranges = new Map<number, ExpRequirementRange>()
  private decisions = new Map<number, ExpAdmission>()
  private sources = new Map<number, ExpExactEvidence>()
  private latestSourceAt = -Infinity
  private pending: Pending | null = null
  private intervals: Interval[] = []
  private reference: ExpAdmissionCheckpoint['reference'] = null
  private recovering = false
  private total = 0
  private epoch = 0
  private epochStartTotal = 0
  private epochStartAt = 0
  private rejectedGap: ExpAdmissionCheckpoint['rejectedGap'] = null

  reset(state: GameStateSnapshot | null = null): void {
    this.anchor = state?.level !== null && state?.level !== undefined && state.exp.absolute !== null
      && state.exp.ratio !== null && !state.exp.estimated && expRequirementRange(state.exp, state.exp.percentagePrecision) !== undefined
      ? { level: state.level, value: state.exp, confidence: state.confidence.exp, sourceAt: state.observedAt?.exp ?? state.capturedAt,
          deliveredAt: state.capturedAt, percentagePrecision: state.exp.percentagePrecision, confirmation: 'consensus' }
      : null
    this.ranges.clear()
    if (this.anchor) {
      const range = state!.exp.requiredExpRange ?? expRequirementRange(state!.exp, state!.exp.percentagePrecision)
      if (range) this.ranges.set(this.anchor.level!, range)
    }
    this.intervals = []
    this.reference = null
    this.total = 0
    this.epoch = 0
    this.epochStartTotal = 0
    this.epochStartAt = this.anchor?.sourceAt ?? 0
    this.rejectedGap = null
    this.breakContinuity()
    this.recovering = false
  }

  checkpoint(): ExpAdmissionCheckpoint {
    return { anchor: this.anchor, ranges: [...this.ranges], intervals: [...this.intervals],
      reference: this.reference, total: this.total, epoch: this.epoch,
      epochStartTotal: this.epochStartTotal, epochStartAt: this.epochStartAt, rejectedGap: this.rejectedGap }
  }

  restore(checkpoint: ExpAdmissionCheckpoint): void {
    this.anchor = checkpoint.anchor
    this.ranges = new Map(checkpoint.ranges)
    this.intervals = [...checkpoint.intervals]
    this.reference = checkpoint.reference
    this.total = checkpoint.total
    this.epoch = checkpoint.epoch
    this.epochStartTotal = checkpoint.epochStartTotal
    this.epochStartAt = checkpoint.epochStartAt
    this.rejectedGap = checkpoint.rejectedGap
    this.breakContinuity()
  }

  breakContinuity(): void {
    this.pending = null
    this.decisions.clear()
    this.sources.clear()
    this.latestSourceAt = this.anchor?.sourceAt ?? -Infinity
    this.recovering = this.anchor !== null
  }

  isPending(): boolean { return this.pending !== null || this.recovering }

  permitsEstimate(level: number | null, ratio: number | null, sourceAt: number): boolean {
    if (!this.anchor || level === null || ratio === null || this.isPending()) return false
    const range = this.ranges.get(level)
    if (!range) return false
    const required = representativeExpRequirement(range)
    const sample: ExpExactEvidence = { level, sourceAt, deliveredAt: sourceAt, confidence: 1, confirmation: 'source-frame',
      value: { absolute: Math.round(required * ratio), ratio, requiredExp: required } }
    if (level === this.anchor.level && ratio < this.anchor.value.ratio! - .0005) return false
    const delta = this.difference(this.anchor, sample)
    return delta !== null && delta <= this.threshold(this.anchor, sample)
  }

  observe(sample: ExpExactEvidence, minimumConfidence = .62): ExpAdmission {
    const range = expRequirementRange(sample.value, sample.percentagePrecision)
    if (!Number.isFinite(sample.confidence) || sample.confidence < minimumConfidence || sample.level === null || !Number.isInteger(sample.level)
      || sample.level < 1 || sample.level > 200 || sample.value.estimated || range === undefined
      || !Number.isFinite(sample.sourceAt) || !Number.isFinite(sample.deliveredAt) || sample.sourceAt > sample.deliveredAt) {
      return { status: 'rejected', reason: 'invalid', samples: 0, creditedDelta: 0 }
    }
    const cached = this.decisions.get(sample.sourceAt)
    const source = this.sources.get(sample.sourceAt)
    if (cached && source) {
      return source.level === sample.level && source.value.absolute === sample.value.absolute
        && source.value.ratio === sample.value.ratio && (source.percentagePrecision ?? 2) === (sample.percentagePrecision ?? 2)
        ? cached : { status: 'rejected', reason: 'stale', samples: 0, creditedDelta: 0 }
    }
    if (sample.sourceAt <= this.latestSourceAt) return { status: 'rejected', reason: 'stale', samples: 0, creditedDelta: 0 }
    if (sample.sourceAt - this.latestSourceAt > 120_000 && this.anchor) this.breakContinuity()
    this.latestSourceAt = sample.sourceAt
    const decision = this.decide(sample, range)
    this.decisions.set(sample.sourceAt, decision)
    this.sources.set(sample.sourceAt, sample)
    for (const at of this.decisions.keys()) {
      if (at < sample.sourceAt - 180_000 || this.decisions.size > 512) {
        this.decisions.delete(at)
        this.sources.delete(at)
      }
    }
    return decision
  }

  private decide(sample: ExpExactEvidence, range: ExpRequirementRange | null): ExpAdmission {
    const anchor = this.anchor
    const known = this.ranges.get(sample.level!)
    const merged = known && range ? intersectExpRequirementRanges([known, range]) : range ?? known ?? null
    if (!anchor) return this.accept(sample, range, 0, 'baseline', 0)
    const delta = this.difference(anchor, sample)
    const limit = this.rejectedGap?.level === sample.level
      ? Math.min(this.threshold(anchor, sample), this.rejectedGap.threshold) : this.threshold(anchor, sample)
    if (delta !== null && delta > limit) this.rejectedGap = { level: sample.level!, threshold: limit }
    let reason: Risk | undefined
    if (known && range && !merged) reason = 'requirement-conflict'
    else if (sample.level !== anchor.level && delta === null) reason = 'level-correction'
    else if (delta !== null && delta < 0) reason = 'decrease'
    else if (delta === null || delta > limit) reason = 'large-gain'
    else if (this.recovering) reason = 'recovery'

    if (!reason) {
      this.pending = null
      return this.accept(sample, merged ?? null, delta!, sample.level === anchor.level ? 'continuous' : 'level-up', limit)
    }

    const previous = this.pending
    const intersection = previous?.range && range ? intersectExpRequirementRanges([previous.range, range]) : range ?? previous?.range ?? null
    const step = previous ? sample.value.absolute! - previous.last.value.absolute! : 0
    // Review growth is subject to the same bound, using the pre-review requirement and rate reference.
    const stepLimit = previous ? this.threshold({ ...anchor, sourceAt: previous.last.sourceAt }, sample) : limit
    const compatible = previous && previous.reason === reason && previous.last.level === sample.level
      && sample.sourceAt > previous.last.sourceAt && sample.sourceAt - previous.last.sourceAt <= 120_000
      && step >= 0 && step <= stepLimit && intersection !== undefined
    const pending: Pending = compatible
      ? { ...previous, last: sample, range: intersection ?? null, count: previous.count + 1, credit: previous.credit + step }
      : { first: sample, last: sample, range, reason, count: 1, credit: 0,
          threshold: reason === 'large-gain' && previous?.reason === reason && previous.last.level === sample.level ? previous.threshold : limit }
    this.pending = pending
    if (pending.count < 3) return { status: 'pending', reason, samples: pending.count, creditedDelta: 0, threshold: pending.threshold }

    const recoveryCredit = reason === 'recovery' && delta !== null && delta >= 0 && delta <= pending.threshold
    const transition = recoveryCredit ? sample.level === anchor.level ? 'continuous' : 'level-up' : 'rebaseline'
    const credit = recoveryCredit ? delta : pending.credit
    if (!recoveryCredit) {
      this.epoch += 1
      this.epochStartTotal = this.total
      this.epochStartAt = pending.first.sourceAt
    }
    this.pending = null
    this.recovering = false
    const confirmedRange = reason === 'requirement-conflict' ? pending.range
      : merged && pending.range ? intersectExpRequirementRanges([merged, pending.range])! : merged ?? pending.range
    const result = this.accept(sample, confirmedRange, credit, transition, pending.threshold, recoveryCredit)
    return { ...result, samples: 3, reason,
      confirmedLoss: sample.level === anchor.level && sample.value.absolute! < anchor.value.absolute!,
      ...(!recoveryCredit ? { rebaseline: pending.first } : {}) }
  }

  private accept(sample: ExpExactEvidence, range: ExpRequirementRange | null, credit: number,
    transition: NonNullable<ExpAdmission['transition']>, threshold: number, learn = true): ExpAdmission {
    if (range) this.ranges.set(sample.level!, range)
    const required = range ? representativeExpRequirement(range, sample.value.absolute!, sample.value.requiredExp
      ?? (sample.value.ratio ? sample.value.absolute! / sample.value.ratio : null)) : null
    const value = { ...sample.value, requiredExp: required, requiredExpApproximate: required !== null,
      requiredExpRange: range, percentagePrecision: sample.percentagePrecision ?? 2 }
    if (learn && this.anchor && credit > 0) {
      const seconds = (sample.sourceAt - this.anchor.sourceAt) / 1000
      if (seconds > 0) this.intervals.push({ at: sample.sourceAt, delta: credit, seconds })
    }
    this.intervals = this.intervals.filter(entry => sample.sourceAt - entry.at <= 120_000).slice(-20)
    if (this.intervals.length) this.reference = {
      delta: median(this.intervals.map(entry => entry.delta)),
      rate: median(this.intervals.map(entry => entry.delta / entry.seconds)),
    }
    this.total += credit
    this.rejectedGap = null
    this.anchor = { ...sample, value }
    return { status: 'accepted', samples: 1, value, creditedDelta: credit, transition, threshold,
      accounting: { total: this.total, epoch: this.epoch, sourceAt: sample.sourceAt,
        epochStartTotal: this.epochStartTotal, epochStartAt: this.epochStartAt } }
  }

  private threshold(anchor: ExpExactEvidence, sample: ExpExactEvidence): number {
    const seconds = Math.max(0, (sample.sourceAt - anchor.sourceAt) / 1000)
    const recent = this.intervals.filter(entry => sample.sourceAt - entry.at <= 120_000)
    const reference = recent.length ? {
      delta: median(recent.map(entry => entry.delta)),
      rate: median(recent.map(entry => entry.delta / entry.seconds)),
    } : this.reference
    if (reference) return Math.max(reference.delta * 8, reference.rate * seconds * 8)
    const currentRange = expRequirementRange(sample.value, sample.percentagePrecision)
    const required = anchor.value.requiredExp ?? this.ranges.get(anchor.level!)?.minimum
      ?? (currentRange ? representativeExpRequirement(currentRange) : 0)
    return Math.min(required * .05 * Math.max(seconds, 1), required * .2)
  }

  private difference(start: ExpExactEvidence, end: ExpExactEvidence): number | null {
    if (end.level === start.level) return end.value.absolute! - start.value.absolute!
    const wrapped = end.level === start.level! + 1
      && (end.value.absolute! < start.value.absolute! || end.value.ratio! < start.value.ratio! - .0002)
    const required = start.value.requiredExp
    return wrapped && required != null && required >= start.value.absolute!
      ? required - start.value.absolute! + end.value.absolute! : null
  }
}

function median(values: number[]): number {
  values.sort((a, b) => a - b)
  const middle = Math.floor(values.length / 2)
  return values.length % 2 ? values[middle]! : (values[middle - 1]! + values[middle]!) / 2
}
